import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import posthog from 'posthog-js';
import { supabase } from '@/integrations/supabase/client';
import { getStoredEndorselyReferral } from '@/lib/endorsely';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, profileData?: {
    phoneNumber: string;
    countryCode: string;
    jobTitle: string;
    yearsExperience: number;
  }) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const identifyPostHogUser = (user: User | null | undefined) => {
  if (!user) {
    posthog.reset();
    return;
  }

  const fullName =
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    user.email ||
    user.id;

  posthog.identify(user.id, {
    email: user.email,
    name: fullName,
  });
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        identifyPostHogUser(session?.user);
        setLoading(false);

        // Check subscription status when user signs in or after successful checkout
        if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          if (navigator.onLine) {
            setTimeout(() => {
              supabase.functions.invoke('check-subscription').catch(console.error);
            }, 0);
          }
          
          // Sync user to Mailchimp audience for email notifications
          if (event === 'SIGNED_IN' && navigator.onLine) {
            setTimeout(() => {
              supabase.functions.invoke('sync-user-to-mailchimp', {
                body: {
                  userId: session.user.id,
                  email: session.user.email,
                  fullName: session.user.user_metadata?.full_name || '',
                },
              }).catch(console.error);
            }, 0);
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      identifyPostHogUser(session?.user);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, profileData?: {
    phoneNumber: string;
    countryCode: string;
    jobTitle: string;
    yearsExperience: number;
  }) => {
    const redirectUrl = `${window.location.origin}/`;
    const endorselyReferralId = getStoredEndorselyReferral();

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          phone_number: profileData?.phoneNumber,
          country_code: profileData?.countryCode,
          job_title: profileData?.jobTitle,
          years_experience: profileData?.yearsExperience,
          endorsely_referral_id: endorselyReferralId,
        },
      },
    });

    if (!error && endorselyReferralId) {
      supabase.functions.invoke('track-referral', {
        body: {
          eventType: 'signup',
          endorselyReferralId,
          email,
          name: fullName,
        },
      }).catch(console.error);
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        console.error("Google sign-in failed:", error);
        return { error };
      }

      return { error: null };
    } catch (error: any) {
      console.error("Google sign-in error:", error);
      return { error };
    }
  };


  const signOut = async () => {
    await supabase.auth.signOut();
    posthog.reset();
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
